import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { KeyRound, Eye, EyeOff, ShieldAlert } from 'lucide-react';

const MIN_LENGTH = 10;

/**
 * Cambio de contraseña, compartido por los dos sistemas de login.
 *
 * `variant` decide contra qué endpoint se habla y a dónde se vuelve, porque
 * usuarios y superadmins viven en colecciones distintas con auth separada.
 */
function ChangePassword({ variant = 'user', setUser }) {
  const navigate = useNavigate();
  const isSuperadmin = variant === 'superadmin';

  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ current: '', next: '', repeat: '' });

  // Motivo por el que estamos aquí: una contraseña puesta por un administrador.
  const forced = localStorage.getItem('must_change_password') === '1';

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (form.next.length < MIN_LENGTH) {
      toast.error(`La contraseña debe tener al menos ${MIN_LENGTH} caracteres`);
      return;
    }
    if (form.next !== form.repeat) {
      toast.error('Las dos contraseñas nuevas no coinciden');
      return;
    }
    if (form.next === form.current) {
      toast.error('La contraseña nueva no puede ser igual a la actual');
      return;
    }

    setLoading(true);
    try {
      const endpoint = isSuperadmin
        ? '/super-admin/change-password'
        : '/auth/change-password';

      await axiosInstance.post(endpoint, {
        current_password: form.current,
        new_password: form.next,
      });

      localStorage.removeItem('must_change_password');
      toast.success('Contraseña actualizada');

      if (isSuperadmin) {
        navigate('/super-admin/dashboard');
      } else {
        // Refrescamos el usuario para que must_change_password quede a false
        try {
          const me = await axiosInstance.get('/auth/me');
          setUser?.(me.data);
        } catch {
          // Si falla el refresco no bloqueamos: la contraseña ya está cambiada
        }
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo cambiar la contraseña');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('must_change_password');
    localStorage.removeItem('superadmin');
    navigate(isSuperadmin ? '/super-admin/login' : '/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-700 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-amber-100 p-3 rounded-full">
              {forced
                ? <ShieldAlert className="h-8 w-8 text-amber-600" />
                : <KeyRound className="h-8 w-8 text-amber-600" />}
            </div>
          </div>
          <CardTitle className="text-2xl">
            {forced ? 'Elige tu contraseña' : 'Cambiar contraseña'}
          </CardTitle>
          <CardDescription>
            {forced
              ? 'Tu contraseña actual la puso un administrador. Elige una propia para continuar.'
              : 'Introduce tu contraseña actual y la nueva.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="current">Contraseña actual</Label>
              <Input
                id="current"
                type={show ? 'text' : 'password'}
                value={form.current}
                onChange={(e) => setForm({ ...form, current: e.target.value })}
                placeholder="La que te han dado"
                required
                autoFocus
              />
            </div>

            <div>
              <Label htmlFor="next">Contraseña nueva</Label>
              <div className="relative">
                <Input
                  id="next"
                  type={show ? 'text' : 'password'}
                  value={form.next}
                  onChange={(e) => setForm({ ...form, next: e.target.value })}
                  placeholder={`Mínimo ${MIN_LENGTH} caracteres`}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShow(!show)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-700"
                  aria-label={show ? 'Ocultar contraseñas' : 'Mostrar contraseñas'}
                >
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {form.next.length > 0 && form.next.length < MIN_LENGTH && (
                <p className="mt-1 text-xs text-amber-600">
                  Faltan {MIN_LENGTH - form.next.length} caracteres
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="repeat">Repite la contraseña nueva</Label>
              <Input
                id="repeat"
                type={show ? 'text' : 'password'}
                value={form.repeat}
                onChange={(e) => setForm({ ...form, repeat: e.target.value })}
                required
              />
              {form.repeat.length > 0 && form.next !== form.repeat && (
                <p className="mt-1 text-xs text-red-600">No coinciden</p>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Guardando...' : 'Guardar contraseña'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={handleLogout}
              className="text-sm text-slate-600 hover:text-slate-900"
            >
              Cerrar sesión
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default ChangePassword;
